import { createContext, useContext } from "react";

import { guessContentType } from "../components/Files/files";
import type { components } from "../generated/forms-api";
import { authHeader, formsFetch, unwrap } from "../lib/formsApi";

import type { Answer } from "./answers";
import type { AnswerSuggestion } from "./prefill";

export type UploadedFile = { fileName: string; fileId: string };

export type FileSuggestions = components["schemas"]["FileSuggestions"];

export type FileActions = {
    upload: (key: string, answer: Answer | undefined, file: File) => Promise<UploadedFile>;
    download: (key: string, fileName: string) => Promise<void>;
    openAttachment: (path: string) => Promise<void>;
    remove: (key: string, fileName: string) => Promise<void>;
    suggest: (fileId: string) => Promise<FileSuggestions>;
    suggestAnswers: (key: string, files: UploadedFile[]) => Promise<AnswerSuggestion[]>;
};

export const FileActionsContext = createContext<FileActions | undefined>(undefined);

export const useFileActions = () => useContext(FileActionsContext);

const VIEWABLE = ["application/pdf", "image/png", "image/jpeg", "text/plain"];

export const showFile = (blob: Blob, fileName: string) => {
    const type = guessContentType(fileName);
    const viewable = VIEWABLE.includes(type);
    const blobUrl = window.URL.createObjectURL(viewable ? new Blob([blob], { type }) : blob);
    const tab = viewable ? window.open(blobUrl, "_blank") : null;
    if (tab) {
        tab.opener = null;
    } else {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    }
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
};

export const downloadFile = async (token: string | undefined, referenceId: string, fileName: string) => {
    const { url } = unwrap(
        await formsFetch.POST("/api/files/download-url", {
            headers: authHeader(token),
            body: { referenceId, fileName },
        }),
        "create download url"
    );
    const response = await fetch(url, {
        headers: { "Content-Type": guessContentType(fileName), "X-Content-Type-Options": "nosniff" },
    });
    if (!response.ok) throw new Error(`Download request failed with status ${response.status}`);
    showFile(await response.blob(), fileName);
};

export const openAttachment = (token: string | undefined, path: string) => {
    const slash = path.indexOf("/");
    if (slash < 0) return Promise.reject(new Error(`attachment path ${path} has no reference`));
    return downloadFile(token, path.slice(0, slash), path.slice(slash + 1));
};
